import { useCallback, useEffect, useState } from 'react';
import { Toast } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import i18n from '../../../i18n';
import { authApi } from '../../../api/auth';
import { ApiEnvelope } from '../../../api/types';
import { AuthUser, useAuthStore } from '../../../stores/auth.store';
import { ProfileRebindFormValues, ProfileRebindState } from './types';

interface RequestError {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
  };
}

function readErrorMessage(error: RequestError, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useProfileRebindModel() {
  const navigate = useNavigate();
  const storeUser = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const [state, setState] = useState<ProfileRebindState>({
    loading: !storeUser,
    submitting: false,
    initialPhone: storeUser?.contact_phone || '',
  });

  const load = useCallback(async () => {
    if (storeUser) {
      return;
    }

    setState((previous) => ({ ...previous, loading: true }));

    try {
      const response = await authApi.me() as { data: ApiEnvelope<AuthUser> };
      const user = response.data.data;
      setUser(user);
      setState({
        loading: false,
        submitting: false,
        initialPhone: user.contact_phone || '',
      });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestError, i18n.t('profile:rebind.loadFailed')),
      });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, [setUser, storeUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async (values: ProfileRebindFormValues) => {
    setState((previous) => ({ ...previous, submitting: true }));

    try {
      const response = await authApi.updateMe({
        contact_phone: values.contact_phone?.trim() || null,
      }) as { data: ApiEnvelope<AuthUser> };
      const user = response.data.data;
      setUser(user);
      setState({
        loading: false,
        submitting: false,
        initialPhone: user.contact_phone || '',
      });
      Toast.show({
        icon: 'success',
        content: i18n.t('profile:rebind.successToast'),
      });
      navigate('/profile', { replace: true });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestError, i18n.t('profile:rebind.submitFailed')),
      });
      setState((previous) => ({ ...previous, submitting: false }));
    }
  }, [navigate, setUser]);

  return {
    state,
    submit,
  };
}
